import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCoin } from '../CoinContext';

const getPath = (path) => {
  const isCampSubdomain = window.location.hostname === 'camp.ssalsoongi.com';
  if (isCampSubdomain) {
    return path.replace(/^\/camp/, '') || '/';
  }
  return path;
};

const THEMES = {
  dark: {
    bg: '#111827',
    cardBg: '#1f2937',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    text: '#e5e7eb',
    textMuted: '#9ca3af',
    myBubble: '#667eea',
    myBubbleText: '#fff',
    theirBubble: '#374151',
    theirBubbleText: '#e5e7eb',
  },
  light: {
    bg: '#ffffff',
    cardBg: '#f3f4f6',
    inputBg: 'rgba(0,0,0,0.05)',
    border: 'rgba(0,0,0,0.1)',
    text: '#1a1a1a',
    textMuted: '#6b7280',
    myBubble: '#667eea',
    myBubbleText: '#fff',
    theirBubble: '#e5e7eb',
    theirBubbleText: '#1f2937',
  },
};

export default function CampGroupChatModal({ isOpen, onClose, groupChatId, postId, postTitle }) {
  const navigate = useNavigate();
  const { walletAddress, nickname } = useCoin();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [chatId, setChatId] = useState(groupChatId);
  const [chatName, setChatName] = useState(postTitle || 'Party Chat');
  const messagesEndRef = useRef(null);
  const subscriptionRef = useRef(null);
  const [theme] = useState(() => {
    const savedMode = localStorage.getItem('campThemeMode');
    const savedTheme = localStorage.getItem('campTheme');
    if (savedMode === 'system' || !savedMode) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return savedTheme || 'dark';
  });

  const colors = THEMES[theme];

  useEffect(() => {
    if (!isOpen || !walletAddress) return;
    loadGroupChat();
    return () => {
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, [isOpen, walletAddress, groupChatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadGroupChat = async () => {
    setLoading(true);
    try {
      const id = groupChatId || chatId;
      if (!id) { setLoading(false); return; }

      // Fetch group chat info
      const { data: chat, error: chatErr } = await supabase
        .from('camp_group_chats')
        .select('*')
        .eq('id', id)
        .single();
      if (chatErr) throw chatErr;
      setChatId(chat.id);
      setChatName(chat.name || 'Party Chat');

      // Fetch members
      const { data: memberData, error: memErr } = await supabase
        .from('camp_group_chat_members')
        .select('*')
        .eq('group_chat_id', id)
        .order('joined_at', { ascending: true });
      if (memErr) throw memErr;
      setMembers(memberData || []);

      // Fetch messages
      const { data: msgs, error: msgErr } = await supabase
        .from('camp_group_chat_messages')
        .select('*')
        .eq('group_chat_id', id)
        .order('created_at', { ascending: true });
      if (msgErr) throw msgErr;
      setMessages(msgs || []);

      // Subscribe to new messages
      const channel = supabase
        .channel(`group_chat_${id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'camp_group_chat_messages',
            filter: `group_chat_id=eq.${id}`,
          },
          (payload) => {
            setMessages((prev) => {
              if (prev.some((m) => m.id === payload.new.id)) return prev;
              const optimisticIdx = prev.findIndex(
                (m) => typeof m.id === 'string' && m.id.startsWith('temp_') &&
                  m.content === payload.new.content &&
                  m.sender_wallet === payload.new.sender_wallet
              );
              if (optimisticIdx !== -1) {
                const updated = [...prev];
                updated[optimisticIdx] = payload.new;
                return updated;
              }
              return [...prev, payload.new];
            });
          }
        )
        .subscribe();

      subscriptionRef.current = channel;
    } catch (err) {
      console.error('Failed to load group chat:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !chatId || sending) return;

    const msgText = newMessage.trim();
    const tempId = `temp_${Date.now()}`;
    const now = new Date().toISOString();
    const senderNickname = nickname || `user_${walletAddress.slice(2, 8)}`;

    const optimisticMsg = {
      id: tempId,
      group_chat_id: chatId,
      sender_wallet: walletAddress,
      sender_nickname: senderNickname,
      content: msgText,
      created_at: now,
    };
    setMessages((prev) => [...prev, optimisticMsg]);
    setNewMessage('');
    setSending(true);

    try {
      const { data, error } = await supabase.from('camp_group_chat_messages').insert({
        group_chat_id: chatId,
        sender_wallet: walletAddress,
        sender_nickname: senderNickname,
        content: msgText,
      }).select().single();

      if (error) throw error;
      setMessages((prev) => prev.map((m) => (m.id === tempId ? data : m)));

      // Update last message on group chat
      await supabase
        .from('camp_group_chats')
        .update({
          last_message: msgText,
          last_message_at: new Date().toISOString(),
        })
        .eq('id', chatId);
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setNewMessage(msgText);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateSeparator = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getDateKey = (dateString) => new Date(dateString).toLocaleDateString();

  // Get color for each member based on their index
  const getMemberColor = (senderWallet) => {
    const memberColors = ['#667eea', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
    const idx = members.findIndex(m => m.wallet_address === senderWallet);
    return memberColors[idx % memberColors.length];
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: colors.bg,
          borderRadius: 16,
          width: '100%',
          maxWidth: 430,
          height: '80vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '14px 16px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>
                Party Chat
              </span>
              <span style={{
                fontSize: 11, color: '#667eea',
                background: 'rgba(102,126,234,0.15)',
                padding: '1px 6px', borderRadius: 6,
              }}>
                {members.length}
              </span>
            </div>
            {chatName && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (postId) {
                    onClose();
                    navigate(getPath(`/camp/post/${postId}`));
                  }
                }}
                style={{
                  fontSize: 11, color: colors.textMuted,
                  maxWidth: 280, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  cursor: postId ? 'pointer' : 'default',
                  textDecoration: postId ? 'underline' : 'none',
                  textDecorationColor: 'rgba(128,128,128,0.4)',
                  textUnderlineOffset: 2,
                }}
              >
                {chatName}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => setShowMembers(!showMembers)}
              style={{
                background: showMembers ? 'rgba(102,126,234,0.15)' : colors.inputBg,
                border: `1px solid ${showMembers ? 'rgba(102,126,234,0.4)' : colors.border}`,
                borderRadius: 8,
                padding: '4px 10px',
                cursor: 'pointer',
                color: showMembers ? '#667eea' : colors.textMuted,
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              Members
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: 22,
                padding: 4,
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Members Panel */}
        {showMembers && (
          <div style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            flexShrink: 0,
          }}>
            {members.map((m, i) => (
              <span
                key={m.id}
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 8,
                  background: colors.inputBg,
                  color: getMemberColor(m.wallet_address),
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {m.nickname || 'Anonymous'}
                {m.character_class && (
                  <span style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>
                    ({m.character_class})
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        {/* Messages Area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40 }}>
              Loading...
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: 40, fontSize: 13 }}>
              Party chat started! Say hello to your team.
            </div>
          ) : (
            messages.map((msg, i) => {
              const isMe = msg.sender_wallet === walletAddress;
              const showDate =
                i === 0 || getDateKey(msg.created_at) !== getDateKey(messages[i - 1].created_at);
              const showSender = !isMe && (
                i === 0 ||
                messages[i - 1].sender_wallet !== msg.sender_wallet ||
                showDate
              );

              return (
                <React.Fragment key={msg.id || i}>
                  {showDate && (
                    <div style={{
                      textAlign: 'center', fontSize: 11,
                      color: colors.textMuted, margin: '12px 0 8px',
                    }}>
                      {formatDateSeparator(msg.created_at)}
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: isMe ? 'flex-end' : 'flex-start',
                      marginTop: showSender ? 8 : 0,
                    }}
                  >
                    {showSender && (
                      <span style={{
                        fontSize: 11,
                        color: getMemberColor(msg.sender_wallet),
                        fontWeight: 600,
                        marginBottom: 2,
                        marginLeft: 4,
                      }}>
                        {msg.sender_nickname || 'Anonymous'}
                      </span>
                    )}
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 6,
                      flexDirection: isMe ? 'row' : 'row',
                    }}>
                      {isMe && (
                        <span style={{ fontSize: 10, color: colors.textMuted, flexShrink: 0 }}>
                          {formatTime(msg.created_at)}
                        </span>
                      )}
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '8px 12px',
                          borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                          backgroundColor: isMe ? colors.myBubble : colors.theirBubble,
                          color: isMe ? colors.myBubbleText : colors.theirBubbleText,
                          fontSize: 13,
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {msg.content}
                      </div>
                      {!isMe && (
                        <span style={{ fontSize: 10, color: colors.textMuted, flexShrink: 0 }}>
                          {formatTime(msg.created_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end',
            flexShrink: 0,
          }}
        >
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1,
              background: colors.inputBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: '10px 14px',
              color: colors.text,
              fontSize: 13,
              outline: 'none',
              resize: 'none',
              maxHeight: 100,
              lineHeight: 1.4,
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            style={{
              background: newMessage.trim()
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : colors.cardBg,
              border: 'none',
              borderRadius: 12,
              padding: '10px 16px',
              cursor: newMessage.trim() ? 'pointer' : 'default',
              color: newMessage.trim() ? '#fff' : colors.textMuted,
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
              opacity: sending ? 0.6 : 1,
            }}
          >
            {sending ? '...' : 'Send'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
